import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useExtractServerError } from 'hooks';
import { ROWS_PER_PAGE_OPTIONS } from 'constants/index';

import GlobalApprovalsUi from './GlobalApprovals.ui';
import useGlobalApprovalsQuery from './useGlobalApprovalsQuery';
import { fromIsoDate, toIsoDate } from '../DirectClaims/format';
import { GlobalApprovalsParams } from './GlobalApprovals.types';

const DEFAULT_ROWS = 50;

function pageNumber(value: string | null, fallback: number) {
  const number = Number(value);
  return value !== null && Number.isInteger(number) && number >= 0
    ? number
    : fallback;
}

function readParams(searchParams: URLSearchParams): GlobalApprovalsParams {
  const rows = pageNumber(searchParams.get('rows'), DEFAULT_ROWS);

  return {
    search: searchParams.get('search') || '',
    status: searchParams.get('status') || '',
    type: searchParams.get('type') || '',
    startDate: searchParams.get('startDate') || '',
    endDate: searchParams.get('endDate') || '',
    first: pageNumber(searchParams.get('first'), 0),
    rows: ROWS_PER_PAGE_OPTIONS.includes(rows) ? rows : DEFAULT_ROWS,
  };
}

// Empty filters stay out of the URL, so a plain link is the full list.
function toSearchParams(params: GlobalApprovalsParams) {
  const entries = Object.entries(params)
    .filter(([, value]) => value !== '')
    .map(([key, value]) => [key, String(value)]);

  return Object.fromEntries(entries);
}

export default function GlobalApprovals() {
  //* Hooks Init
  const [searchParams, setSearchParams] = useSearchParams();

  /*
   * The URL is the query: filters and page, so a shared link opens the same
   * page of approvals. With nothing in it the list starts at the newest
   * request.
   */
  const params = useMemo(() => readParams(searchParams), [searchParams]);

  //* Local State
  const [search, setSearch] = useState(params.search);
  const [dates, setDates] = useState<(Date | null)[]>([
    fromIsoDate(params.startDate),
    fromIsoDate(params.endDate),
  ]);

  const approvalsQuery = useGlobalApprovalsQuery(params);
  const { extractErrorMessage } = useExtractServerError();

  const approvals = approvalsQuery.data?.data.data ?? [];
  const totalRecords = approvalsQuery.data?.data.total ?? 0;

  //* Handlers
  // Any new filter starts again from the first page.
  function update(next: Partial<GlobalApprovalsParams>) {
    setSearchParams(toSearchParams({ ...params, first: 0, ...next }));
  }

  function handleSearchChange(_name: string, value: string) {
    setSearch(value);
  }

  function handleSubmitSearch() {
    update({ search: search.trim() });
  }

  function handleDateChange(event: any) {
    if (!Array.isArray(event.value)) {
      setDates([null, null]);
      update({ startDate: '', endDate: '' });
      return;
    }

    setDates(event.value);

    // The calendar reports a half-picked range after the first click; wait
    // for both ends before asking again.
    const [start, end] = event.value;
    if (start && end) {
      update({ startDate: toIsoDate(start), endDate: toIsoDate(end) });
    }
  }

  function handleStatusChange(value: string) {
    update({ status: value });
  }

  function handleTypeChange(value: string) {
    update({ type: value });
  }

  function handlePageChange(event: { first: number; rows: number }) {
    setSearchParams(
      toSearchParams({ ...params, first: event.first, rows: event.rows })
    );
  }

  return (
    <GlobalApprovalsUi
      approvals={approvals}
      totalRecords={totalRecords}
      loading={approvalsQuery.isFetching}
      errorMessage={
        approvalsQuery.isError ? extractErrorMessage(approvalsQuery.error) : ''
      }
      search={search}
      params={params}
      dates={dates}
      handlers={{
        handleSearchChange,
        handleSubmitSearch,
        handleDateChange,
        handleStatusChange,
        handleTypeChange,
        handlePageChange,
      }}
    />
  );
}
